import {Component, ContentChild, Input, OnInit, TemplateRef} from "@angular/core";
import {DxPageHeaderDirective} from "../../directives/page/dx-page-header.directive";
import {DxPageFooterDirective} from "../../directives/page/dx-page-footer.directive";
import {DxPageSideDirective} from "../../directives/page/dx-page-side.directive";
import {DxPageContentDirective} from "../../directives/page/dx-page-content.directive";
import {
  DxPageContentContext, DxPageFooterContext, DxPageGreetings, DxPageHeaderContext,
  DxPageSideContext
} from "../../directives/page/dx-page.interfaces";
import {DxPageOptions} from "../../../domain/ui-options/page/dx-page-options";


/**
 *  Компонент предоставляет базовую разметку страницы с возможностью переопределения составляющих через шаблоны.
 *  @publicApi
 */
@Component({
  selector: 'dx-page',
  templateUrl: './dx-page.component.html',
  styleUrls: ['./dx-page.component.css']
})
export class DxPageComponent implements OnInit {
  public pageStyles: string[];
  public pageHeaderStyles: string[];
  public pageBodyStyles: string[];
  public pageSideStyles: string[];
  public pageContentStyles: string[];
  public pageFooterStyles: string[];
  public showPageSide: boolean = false;

  /**
   * Объект с дефолными названиями для блоков разметки (просто для наглядности).
   * @name _greetings
   * @type {DxPageGreetings}
   */
  private _greetings: DxPageGreetings;

  /**
   * Опции страницы.
   * @name _options
   * @type {DxPageOptions}
   */
  private _options: DxPageOptions;

  /**
   * Кастомный шаблон шапки страницы.
   * @name pageHeaderTemplate
   * @type {DxPageHeaderDirective}
   */
  @ContentChild(DxPageHeaderDirective, {static: true, read: TemplateRef})
  public pageHeaderTemplate: DxPageHeaderDirective;

  /**
   * Кастомный шаблон боковой панели страницы.
   * @name pageSideTemplate
   * @type {DxPageSideDirective}
   */
  @ContentChild(DxPageSideDirective, {static: true, read: TemplateRef})
  public pageSideTemplate: DxPageSideDirective;

  /**
   * Кастомный шаблон области контента страницы.
   * @name pageContentTemplate
   * @type {DxPageContentDirective}
   */
  @ContentChild(DxPageContentDirective, {static: true, read: TemplateRef})
  public pageContentTemplate: DxPageContentDirective;

  /**
   * Кастомный шаблон футера страницы.
   * @name pageFooterTemplate
   * @type {DxPageFooterDirective}
   */
  @ContentChild(DxPageFooterDirective, {static: true, read: TemplateRef})
  public pageFooterTemplate: DxPageFooterDirective;

  /**
   * @constructor
   */
  constructor() {
    this._options = new DxPageOptions();
    this._greetings = {
      headerGreeting: 'DEFAULT DX-PAGE-HEADER',
      sideGreeting: 'DEFAULT DX-PAGE-SIDE',
      contentGreeting: 'DEFAULT DX-PAGE-CONTENT',
      footerGreeting: 'DEFAULT DX-PAGE-FOOTER'
    }
  }

  @Input()
  set options(value: DxPageOptions) {
    this._options = value;
  }

  get options(): DxPageOptions {
    return this._options;
  }

  @Input()
  set greetings(value: DxPageGreetings) {
    this._greetings = value;
  }

  get greetings(): DxPageGreetings {
    return this._greetings;
  }

  ngOnInit(): void {
    this.pageStyles = this._options.pageStyles;
    this.pageHeaderStyles = this._options.pageHeaderStyles;
    this.pageBodyStyles = this._options.pageBodyStyles;
    this.pageSideStyles = this._options.pageSideStyles;
    this.pageContentStyles = this._options.pageContentStyles;
    this.pageFooterStyles = this._options.pageFooterStyles;
    this.showPageSide = !this._options.withoutPageSide;
  }


  /**
   * Создает контекст щаблона заголовка.
   * @return {DxPageHeaderContext} - контекст щаблона заголовка.
   * @public
   */
  public get pageHeaderContext(): DxPageHeaderContext {
    return {
      greeting: this._greetings.headerGreeting
    }
  }

  /**
   * Создает контекст щаблона боковой панели.
   * @return {DxPageSideContext} - контекст щаблона боковой панели.
   * @public
   */
  public get pageSideContext(): DxPageSideContext {
    return {
      greeting: this._greetings.sideGreeting
    }
  }

  /**
   * Создает контекст щаблона области контента страницы.
   * @return {DxPageContentContext} - контекст щаблона области контента страницы.
   * @public
   */
  public get pageContentContext(): DxPageContentContext {
    return {
      greeting: this._greetings.contentGreeting
    }
  }

  /**
   * Создает контекст шаблона футера страницы.
   * @return {DxPageFooterContext} - контекст шаблона футера страницы.
   * @public
   */
  public get pageFooterContext(): DxPageFooterContext {
    return {
      greeting: this._greetings.footerGreeting
    }
  }
}
